/**
 * GameStateContext
 *
 * Provides shared game state (room, players, phase, etc.) to GamePage and all
 * phase components. Fixes the issue where each consumer had its own
 * state—e.g. LobbyPhase's players were always [] because its hook instance
 * never received setRoom from the join response.
 *
 * Used by:
 * - GamePage, LobbyPhase, useGamePhase, useGameHistory, useMonsters, useBoard
 */

import React, {
    createContext,
    useState,
    useEffect,
    useRef,
    useCallback,
    useMemo,
} from 'react';
import useGameSocket from '../hooks/useGameSocket';
import type {
    RoomData,
    GameState,
    RoomPlayer,
    GamePlayer,
    PlayerLetter,
    GamePhase,
    Theme,
    ChatMessage,
} from '../types/game.types';
import type {
    RoomUpdatePayload,
    PlayerJoinedPayload,
    PlayerLeftPayload,
    PlayerReadyUpdatePayload,
    PhaseChangedPayload,
    IslandsPlacedPayload,
    MonsterPlacedPayload,
    MonsterMovedPayload,
    GameEndedPayload,
    TurnChangedPayload,
    PlayerReconnectedPayload,
    PlayerDisconnectedPayload,
    ChatMessageReceivedPayload,
} from '../types/socket.types';

/** Must match `TTL.DISCONNECT_TIMER` in backend (seconds). */
const DISCONNECT_COUNTDOWN_SECONDS = 62;

export interface UseGameStateReturn {
    room: RoomData | null;
    roomCode: string | null;
    gameState: GameState | null;
    phase: GamePhase;
    board: GameState['board'] | null;
    players: RoomPlayer[];
    playerA: RoomPlayer | null;
    playerB: RoomPlayer | null;
    myPlayerLetter: PlayerLetter | null;
    opponentPlayerLetter: PlayerLetter | null;
    myPlayer: RoomPlayer | null;
    opponentPlayer: RoomPlayer | null;
    myGamePlayer: GamePlayer | null;
    opponentGamePlayer: GamePlayer | null;
    currentTurn: number;
    currentPlayer: PlayerLetter | null;
    isMyTurn: boolean;
    winner: PlayerLetter | null;
    isGameEnded: boolean;
    isBotGame: boolean;
    theme: Theme;
    disconnectTimer: {
        active: boolean;
        secondsRemaining: number;
        disconnectedPlayer: PlayerLetter | null;
    };
    chatMessages: ChatMessage[];
    setChatMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>;
    addChatMessage: (payload: ChatMessageReceivedPayload) => void;
    setRoom: (room: RoomData) => void;
    setGameState: (gameState: GameState) => void;
    setPhase: (phase: GamePhase) => void;
    setMyPlayerLetter: (letter: PlayerLetter) => void;
    setTheme: (theme: Theme) => void;
    getPlayer: (playerLetter: PlayerLetter) => RoomPlayer | null;
    getGamePlayer: (playerLetter: PlayerLetter) => GamePlayer | null;
    resetDisconnectTimer: () => void;
}

const defaultDisconnectTimer = {
    active: false,
    secondsRemaining: DISCONNECT_COUNTDOWN_SECONDS,
    disconnectedPlayer: null as PlayerLetter | null,
};

export const GameStateContext = createContext<UseGameStateReturn | null>(null);

export interface GameStateProviderProps {
    children: React.ReactNode;
}

export const GameStateProvider: React.FC<GameStateProviderProps> = ({ children }) => {
    const socket = useGameSocket();
    const socketRef = useRef(socket);
    socketRef.current = socket;

    const [room, setRoomState] = useState<RoomData | null>(null);
    const [currentRoomCode, setCurrentRoomCode] = useState<string | null>(null);
    const [gameState, setGameStateState] = useState<GameState | null>(null);
    const [phase, setPhaseState] = useState<GamePhase>('LOBBY');
    const [theme, setThemeState] = useState<Theme>('ocean');
    const myPlayerLetterRef = useRef<PlayerLetter | null>(null);
    const [myPlayerLetter, setMyPlayerLetterState] = useState<PlayerLetter | null>(null);
    const [disconnectTimer, setDisconnectTimer] = useState<{
        active: boolean;
        secondsRemaining: number;
        disconnectedPlayer: PlayerLetter | null;
    }>(defaultDisconnectTimer);
    const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);

    const players = room?.players ?? [];
    const playerA = players.find((p) => p.id === 'A') ?? null;
    const playerB = players.find((p) => p.id === 'B') ?? null;
    const opponentPlayerLetter = myPlayerLetter === 'A' ? 'B' : myPlayerLetter === 'B' ? 'A' : null;
    const myPlayer = myPlayerLetter ? players.find((p) => p.id === myPlayerLetter) ?? null : null;
    const opponentPlayer =
        opponentPlayerLetter ? players.find((p) => p.id === opponentPlayerLetter) ?? null : null;
    const board = gameState?.board ?? null;
    const currentTurn = gameState?.turn?.number ?? 0;
    const currentPlayer = gameState?.turn?.currentPlayer ?? null;
    const isMyTurn = currentPlayer === myPlayerLetter;
    const winner = gameState?.winner ?? null;
    const isGameEnded =
        gameState?.phase === 'ENDED' ||
        gameState?.status === 'COMPLETED' ||
        gameState?.status === 'ABANDONED';
    const isBotGame = room?.botGame ?? false;
    const myGamePlayer =
        gameState && myPlayerLetter ? gameState.players[myPlayerLetter] ?? null : null;
    const opponentGamePlayer =
        gameState && opponentPlayerLetter ? gameState.players[opponentPlayerLetter] ?? null : null;

    const resetDisconnectTimer = useCallback(() => {
        setDisconnectTimer({ ...defaultDisconnectTimer });
    }, []);

    // Clear chat when room changes (leave or switch room)
    useEffect(() => {
        setChatMessages([]);
    }, [currentRoomCode]);

    const addChatMessage = useCallback((payload: ChatMessageReceivedPayload) => {
        setChatMessages((prev) => [
            ...prev,
            {
                id: `${payload.timestamp}-${payload.playerId}`,
                username: payload.username,
                content: payload.message,
                timestamp: payload.timestamp,
                playerLetter: payload.playerLetter ?? undefined,
                playerId: payload.playerId,
            },
        ]);
    }, []);

    useEffect(() => {
        const unsubscribers: (() => void)[] = [];

        unsubscribers.push(
            socketRef.current.onRoomUpdate((payload: RoomUpdatePayload) => {
                setRoomState(payload.room);
                setCurrentRoomCode(payload.room.code);
                setThemeState(payload.room.theme ?? 'ocean');
                // When server sends gameState (e.g. on join/reconnect to a game), use it for phase and game state
                if (payload.gameState) {
                    setGameStateState(payload.gameState);
                    setPhaseState(payload.gameState.phase);
                } else if (payload.room.phase === 'LOBBY') {
                    // Only set phase from room when in lobby; room updates without gameState (e.g. theme/color) must not overwrite gamePhase
                    setPhaseState('LOBBY');
                }
                // else: room is ACTIVE/ENDED but no gameState in payload — keep current phase (e.g. ISLAND_PLACEMENT)
            })
        );
        unsubscribers.push(
            socketRef.current.onPlayerJoined((payload: PlayerJoinedPayload) => {
                setRoomState(payload.room);
            })
        );
        unsubscribers.push(
            socketRef.current.onPlayerLeft((payload: PlayerLeftPayload) => {
                setRoomState(payload.room);
            })
        );
        unsubscribers.push(
            socketRef.current.onPlayerReadyUpdate((payload: PlayerReadyUpdatePayload) => {
                setRoomState(payload.room);
            })
        );
        unsubscribers.push(
            socketRef.current.onPhaseChanged((payload: PhaseChangedPayload) => {
                setPhaseState(payload.phase);
                setGameStateState(payload.gameState);
            })
        );
        unsubscribers.push(
            socketRef.current.onIslandsPlaced((payload: IslandsPlacedPayload) => {
                setGameStateState(payload.gameState);
                if (payload.phase) setPhaseState(payload.phase);
            })
        );
        unsubscribers.push(
            socketRef.current.onMonsterPlaced((payload: MonsterPlacedPayload) => {
                setGameStateState(payload.gameState);
                if (payload.phase) setPhaseState(payload.phase);
            })
        );
        unsubscribers.push(
            socketRef.current.onMonsterMoved((payload: MonsterMovedPayload) => {
                setGameStateState(payload.gameState);
                if (payload.gameState?.phase) setPhaseState(payload.gameState.phase);
            })
        );
        unsubscribers.push(
            socketRef.current.onGameEnded((payload: GameEndedPayload) => {
                setGameStateState(payload.gameState);
                setPhaseState(payload.gameState.phase);
                resetDisconnectTimer();
            })
        );
        unsubscribers.push(
            socketRef.current.onTurnChanged((payload: TurnChangedPayload) => {
                setGameStateState(payload.gameState);
            })
        );
        unsubscribers.push(
            socketRef.current.onPlayerReconnected((payload: PlayerReconnectedPayload) => {
                setRoomState(payload.room);
                resetDisconnectTimer();
            })
        );
        unsubscribers.push(
            socketRef.current.onPlayerDisconnected((payload: PlayerDisconnectedPayload) => {
                setRoomState(payload.room);
                // 1a: local countdown for the remaining player when the opponent drops (server also runs a 60s Redis timer)
                const me = myPlayerLetterRef.current;
                const { phase: roomPhase } = payload.room;
                if (
                    me &&
                    payload.playerLetter !== me &&
                    roomPhase !== 'LOBBY' &&
                    roomPhase !== 'ENDED'
                ) {
                    setDisconnectTimer({
                        active: true,
                        secondsRemaining: DISCONNECT_COUNTDOWN_SECONDS,
                        disconnectedPlayer: payload.playerLetter,
                    });
                }
            })
        );
        unsubscribers.push(
            socketRef.current.onChatMessage((payload: ChatMessageReceivedPayload) => {
                setChatMessages((prev) => [
                    ...prev,
                    {
                        id: `${payload.timestamp}-${payload.playerId}`,
                        username: payload.username,
                        content: payload.message,
                        timestamp: payload.timestamp,
                        playerLetter: payload.playerLetter ?? undefined,
                        playerId: payload.playerId,
                    },
                ]);
            })
        );

        return () => {
            unsubscribers.forEach((u) => u());
        };
    }, [resetDisconnectTimer]);

    useEffect(() => {
        if (!disconnectTimer.active) return;
        if (disconnectTimer.secondsRemaining <= 0) {
            setDisconnectTimer((prev) => ({ ...prev, active: false }));
            return;
        }
        const interval = setInterval(() => {
            setDisconnectTimer((prev) => ({
                ...prev,
                secondsRemaining: Math.max(0, prev.secondsRemaining - 1),
            }));
        }, 1000);
        return () => clearInterval(interval);
    }, [disconnectTimer.active, disconnectTimer.secondsRemaining]);

    const setRoom = useCallback((newRoom: RoomData) => {
        setRoomState(newRoom);
        setCurrentRoomCode(newRoom.code);
        setPhaseState(newRoom.phase ?? 'LOBBY');
        setThemeState(newRoom.theme ?? 'ocean');
    }, []);

    const setGameState = useCallback((newGameState: GameState) => {
        setGameStateState(newGameState);
        setPhaseState(newGameState.phase);
    }, []);

    const setPhase = useCallback((newPhase: GamePhase) => {
        setPhaseState(newPhase);
    }, []);

    const setMyPlayerLetter = useCallback((letter: PlayerLetter) => {
        myPlayerLetterRef.current = letter;
        setMyPlayerLetterState(letter);
    }, []);

    const setTheme = useCallback((newTheme: Theme) => {
        setThemeState(newTheme);
    }, []);

    const getPlayer = useCallback(
        (playerLetter: PlayerLetter): RoomPlayer | null => {
            return players.find((p) => p.id === playerLetter) ?? null;
        },
        [players]
    );

    const getGamePlayer = useCallback(
        (playerLetter: PlayerLetter): GamePlayer | null => {
            return gameState?.players[playerLetter] ?? null;
        },
        [gameState]
    );

    const value = useMemo<UseGameStateReturn>(
        () => ({
            room,
            roomCode: currentRoomCode,
            gameState,
            phase,
            board,
            players,
            playerA,
            playerB,
            myPlayerLetter,
            opponentPlayerLetter,
            myPlayer,
            opponentPlayer,
            myGamePlayer,
            opponentGamePlayer,
            currentTurn,
            currentPlayer,
            isMyTurn,
            winner,
            isGameEnded,
            isBotGame,
            theme,
            disconnectTimer,
            chatMessages,
            setChatMessages,
            addChatMessage,
            setRoom,
            setGameState,
            setPhase,
            setMyPlayerLetter,
            setTheme,
            getPlayer,
            getGamePlayer,
            resetDisconnectTimer,
        }),
        [
            room,
            currentRoomCode,
            gameState,
            phase,
            board,
            players,
            playerA,
            playerB,
            myPlayerLetter,
            opponentPlayerLetter,
            myPlayer,
            opponentPlayer,
            myGamePlayer,
            opponentGamePlayer,
            currentTurn,
            currentPlayer,
            isMyTurn,
            winner,
            isGameEnded,
            isBotGame,
            theme,
            disconnectTimer,
            chatMessages,
            setChatMessages,
            addChatMessage,
            setRoom,
            setGameState,
            setPhase,
            setMyPlayerLetter,
            setTheme,
            getPlayer,
            getGamePlayer,
            resetDisconnectTimer,
        ]
    );

    return (
        <GameStateContext.Provider value={value}>{children}</GameStateContext.Provider>
    );
};
